import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Grid,
  CircularProgress,
  Alert,
  TextField,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CheckCircle as SuccessIcon,
  ErrorOutline as ErrorOutlineIcon,
  Schedule as ScheduleIcon,
  PlayArrow as ForceRunIcon,
  Replay as ReplayIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Widgets as WidgetsIcon,
  Search as SearchIcon,
  CloudUpload as CloudUploadIcon,
  Build as BuildIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';

import WidgetSnapshotDialog from './WidgetSnapshotDialog';
import { useTranslation } from 'react-i18next';
import CloseButton from './Common/CloseButton';
import { useWebSocket } from './WebSocketContext';
import { apiRequest } from '../Utils/apiClient';
import { fetchActionLogs } from '../Utils/actionsApi';

// Theme colors
const primaryColor = '#5757b0';
const hoverColor = '#7777d0';
const successColor = '#4caf50';
const errorColor = '#f44336';
const warningColor = '#ff9800';

// Column widths (px) - used for consistent alignment
const columnWidths = {
  timestamp: 180,
  type: 64,
  status: 68,
  context: 140,
  duration: 90,
  file: 220,
  details: 300
};
const tableMinWidth = Object.values(columnWidths).reduce((total, value) => total + value, 0);

// Compact UI sizes
const compactFontSize = '0.72rem';
const compactHeadFontSize = '0.78rem';
const compactIconSize = 18;
const compactCellPadding = '6px 8px';

const EMPTY_FILTERS = Object.freeze({});

export const formatActionsLogsDialogTitle = (value, maxLength = 100) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) {
    return 'Event Logs';
  }
  if (rawValue.length <= maxLength) {
    return rawValue;
  }
  return `...${rawValue.slice(-maxLength)}`;
};

export const getActionLogDetailsSummary = (log = {}) => {
  const status = String(log.status || '').toLowerCase();
  const isSkipped = status === 'skipped' || status === 'cached';
  const errorDetails = String(log.error_details || '').trim();
  const successDetails = String(log.success_details || '').trim();

  if (errorDetails) {
    return {
      kind: isSkipped ? 'skipped' : 'error',
      details: errorDetails
    };
  }

  if (successDetails) {
    return {
      kind: 'success',
      details: successDetails
    };
  }

  return {
    kind: 'none',
    details: ''
  };
};

export const getActionLogDetailsPreview = (log = {}, maxLength = 140) => {
  const { kind, details } = getActionLogDetailsSummary(log);
  if (kind === 'none') {
    return {
      kind,
      details,
      preview: ''
    };
  }

  const firstLine = details.split(/\r?\n/).find((line) => line.trim()) || details;
  const normalizedPreview = firstLine.trim();
  const preview = normalizedPreview.length > maxLength
    ? `${normalizedPreview.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`
    : normalizedPreview;

  return {
    kind,
    details,
    preview
  };
};

const ActionsLogsDialog = ({
  open, 
  onClose, 
  title = "Event Logs",
  caseid = null,
  timeevent_id = null,
  showFilters = true,
  maxHeight = '600px',
  maxLogs = 20,
  defaultShowAll = false,
  extraFilters = EMPTY_FILTERS
}) => {
  const { t } = useTranslation();
  const { isConnected } = useWebSocket();
  const previousConnectionStateRef = useRef(isConnected);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [, setPage] = useState(1);
  const [cursor, setCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [previousCursors, setPreviousCursors] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    totalCount: 0,
    pageSize: maxLogs,
    hasMore: false
  });
  
  // Selection state
  const [selectedCaseId, setSelectedCaseId] = useState(caseid || '');
  const [selectedTimeventId, setSelectedTimeventId] = useState(timeevent_id || '');
  
  // Available options
  const [availableCases, setAvailableCases] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  
  const [localFilters, setLocalFilters] = useState({
    status: '',
    actionType: '',
    executionContext: ''
  });
  const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
  const [snapshotContext, setSnapshotContext] = useState(null);

  const normalizedExtraFilters = (extraFilters && typeof extraFilters === 'object')
    ? extraFilters
    : EMPTY_FILTERS;

  const appliedExtraFilters = useMemo(() => {
    if (normalizedExtraFilters === EMPTY_FILTERS) {
      return EMPTY_FILTERS;
    }

    const filtered = Object.entries(normalizedExtraFilters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    return Object.keys(filtered).length ? filtered : EMPTY_FILTERS;
  }, [normalizedExtraFilters]);

  
  // Derive current page size based on context filters
  const effectiveLimit = useMemo(() => (
    (!selectedCaseId && !selectedTimeventId) ? Math.max(maxLogs, 50) : maxLogs
  ), [selectedCaseId, selectedTimeventId, maxLogs]);

  useEffect(() => {
    setPaginationInfo(prev => ({
      ...prev,
      pageSize: effectiveLimit
    }));
  }, [effectiveLimit]);

  // Fetch action logs with selected parameters
  const fetchActionLogsData = useCallback(async (options = {}) => {
    if (!open) return;
    const {
      cursorOverride = null,
      previousStackOverride = []
    } = options;

    setLoading(true);
    setError('');
    try {
      const payload = await fetchActionLogs({
        requestId: `action_logs_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        caseid: selectedCaseId || undefined,
        timeevent_id: selectedTimeventId || undefined,
        status: localFilters.status || undefined,
        action_type: localFilters.actionType || undefined,
        execution_context: localFilters.executionContext || undefined,
        limit: effectiveLimit,
        pagination_mode: 'cursor',
        cursor: cursorOverride ?? null,
        ...appliedExtraFilters
      });

      const receivedLogs = payload.logs || [];
      const totalCount = Number(payload.total_count) || 0;
      const responseHasMore = Boolean(payload.has_more);
      const responseNextCursor = payload.next_cursor ?? null;
      const responseCursor = payload.cursor ?? (cursorOverride ?? null);
      const responsePreviousCursors = Array.isArray(previousStackOverride) ? [...previousStackOverride] : [];
      const responsePage = responsePreviousCursors.length + 1;
      const rawPageSize = payload.page_size ?? payload.limit ?? (receivedLogs.length || effectiveLimit);
      const safePageSize = rawPageSize && rawPageSize > 0 ? rawPageSize : maxLogs;

      setLogs(receivedLogs);
      setCursor(responseCursor);
      setNextCursor(responseNextCursor);
      setPreviousCursors(responsePreviousCursors);
      setPaginationInfo({
        page: responsePage,
        totalCount,
        pageSize: safePageSize,
        hasMore: responseHasMore
      });
      setPage(responsePage);
      setError('');
    } catch (error) {
      setError(error?.message || 'Failed to load action logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [appliedExtraFilters, effectiveLimit, localFilters.actionType, localFilters.executionContext, localFilters.status, maxLogs, open, selectedCaseId, selectedTimeventId]);

  const handlePageChange = useCallback((direction) => {
    if (direction === 'next') {
      if (!paginationInfo.hasMore || !nextCursor) {
        return;
      }
      const nextPreviousCursors = [...previousCursors, cursor ?? null];
      fetchActionLogsData({
        cursorOverride: nextCursor,
        previousStackOverride: nextPreviousCursors
      });
      return;
    }

    if (direction === 'prev') {
      if (!previousCursors.length) {
        return;
      }
      const targetCursor = previousCursors[previousCursors.length - 1];
      const nextPreviousCursors = previousCursors.slice(0, -1);
      fetchActionLogsData({
        cursorOverride: targetCursor ?? null,
        previousStackOverride: nextPreviousCursors
      });
      return;
    }

    if (direction === 'refresh') {
      fetchActionLogsData({
        cursorOverride: cursor ?? null,
        previousStackOverride: previousCursors
      });
      return;
    }

    fetchActionLogsData({
      cursorOverride: null,
      previousStackOverride: []
    });
  }, [fetchActionLogsData, paginationInfo.hasMore, nextCursor, previousCursors, cursor]);

  // Initialize when dialog opens
  useEffect(() => {
    if (open) {
      const initialLimit = (!caseid && !timeevent_id) ? Math.max(maxLogs, 50) : maxLogs;
      setSelectedCaseId(caseid || '');
      setSelectedTimeventId(timeevent_id || '');
      setError('');
      setLogs([]);
      setPage(1);
      setCursor(null);
      setNextCursor(null);
      setPreviousCursors([]);
      setPaginationInfo(prev => ({
        ...prev,
        page: 1,
        totalCount: 0,
        pageSize: initialLimit,
        hasMore: false
      }));

      setLoadingOptions(true);
      apiRequest('/api/v1/sidebar/projects?page=1&limit=200')
        .then((payload) => {
          const casesPayload = Array.isArray(payload?.cases) ? payload.cases : [];
          setAvailableCases(casesPayload);
        })
        .catch((error) => {
          console.error('ActionsLogsDialog: Failed to load cases:', error);
          setAvailableCases([]);
        })
        .finally(() => {
          setLoadingOptions(false);
        });

      fetchActionLogsData({
        cursorOverride: null,
        previousStackOverride: []
      });
    }
  }, [appliedExtraFilters, caseid, fetchActionLogsData, maxLogs, open, timeevent_id]);

  useEffect(() => {
    const wasConnected = previousConnectionStateRef.current;
    previousConnectionStateRef.current = isConnected;

    if (!open) {
      return;
    }

    if (!isConnected) {
      setSnapshotDialogOpen(false);
      setSnapshotContext(null);
      return;
    }

    if (wasConnected === false) {
      setSnapshotDialogOpen(false);
      setSnapshotContext(null);
      fetchActionLogsData({
        cursorOverride: null,
        previousStackOverride: []
      });
    }
  }, [fetchActionLogsData, isConnected, open]);
  
  // Clear timeevent input when case selection changes
  useEffect(() => {
    if (open && selectedCaseId) {
      // Clear timeevent ID when case changes
      setSelectedTimeventId('');
      setPage(1);
      setCursor(null);
      setNextCursor(null);
      setPreviousCursors([]);
      setPaginationInfo(prev => ({
        ...prev,
        page: 1,
        hasMore: false
      }));
    }
  }, [selectedCaseId, open]);
  
  // Note: Auto-fetch removed to prevent multiple requests
  // Users should use "Apply Filters" or "Show All Actions" button to trigger fetches

  const handleFilterChange = (filterType, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const applyFilters = () => {
    handlePageChange('reset');
  };

  const clearFilters = () => {
    setLocalFilters({
      status: '',
      actionType: '',
      executionContext: ''
    });
    setTimeout(() => handlePageChange('reset'), 100);
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'SUCCESS':
        return <SuccessIcon sx={{ color: successColor, fontSize: compactIconSize }} />;
      case 'FAILED':
      case 'ERROR':
        return <ErrorOutlineIcon sx={{ color: errorColor, fontSize: compactIconSize }} />;
      case 'SKIPPED':
        return <ScheduleIcon sx={{ color: warningColor, fontSize: compactIconSize }} />;
      case 'TRIGGERED':
        return <ForceRunIcon sx={{ color: primaryColor, fontSize: compactIconSize }} />;
      default:
        return <ScheduleIcon sx={{ color: '#999', fontSize: compactIconSize }} />;
    }
  };

  const renderStatusIndicator = (status) => {
    const tooltipTitle = formatDisplayValue(status, { transformUnderscores: true });
    return (
      <Tooltip title={tooltipTitle} arrow enterTouchDelay={0} leaveTouchDelay={2500}>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: compactIconSize,
            minHeight: compactIconSize,
            cursor: 'help'
          }}
          aria-label={tooltipTitle}
        >
          {getStatusIcon(status)}
        </Box>
      </Tooltip>
    );
  };

  const formatDisplayValue = (value, { transformUnderscores = false } = {}) => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }

    const rawValue = String(value);
    return transformUnderscores ? rawValue.replace(/_/g, ' ') : rawValue;
  };

  const renderTooltipText = (value, { title, transformUnderscores = false, sx = {} } = {}) => {
    const displayValue = formatDisplayValue(value, { transformUnderscores });
    const tooltipTitle = title ?? displayValue;

    return (
      <Tooltip title={tooltipTitle} arrow>
        <Typography
          variant="body2"
          sx={{
            fontSize: compactFontSize,
            cursor: 'help',
            ...sx
          }}
        >
          {displayValue}
        </Typography>
      </Tooltip>
    );
  };

  const renderDetailsTooltip = (label, details) => (
    <Box sx={{ maxWidth: 720, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.5,
          color: 'rgba(255, 255, 255, 0.72)',
          letterSpacing: '0.01em'
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: compactFontSize,
          whiteSpace: 'inherit'
        }}
      >
        {details}
      </Typography>
    </Box>
  );

  const renderLogDetails = (log) => {
    const { kind, details, preview } = getActionLogDetailsPreview(log);
    const hasDetails = kind !== 'none';
    const detailLabel = hasDetails
      ? (
        kind === 'error'
          ? t('actionsLogs.errorDetails', 'Error details')
          : kind === 'success'
            ? t('actionsLogs.successDetails', 'Success details')
            : t('actionsLogs.skippedDetails', 'Skipped details')
      )
      : t('actionsLogs.noDetails', 'No details');
    const tooltipLabel = hasDetails
      ? (
        kind === 'error'
          ? t('actionsLogs.errorDetails', 'Error details')
          : kind === 'success'
            ? t('actionsLogs.successDetails', 'Success details')
            : t('actionsLogs.skippedDetails', 'Skipped details')
      )
      : t('actionsLogs.noDetailsTooltip', 'No additional details available');
    const detailColor = hasDetails
      ? (
        kind === 'success'
          ? successColor
          : kind === 'error'
            ? errorColor
            : warningColor
      )
      : '#999';
    const detailTooltip = hasDetails
      ? renderDetailsTooltip(tooltipLabel, details)
      : tooltipLabel;

    const detailContent = (
      <Tooltip title={detailTooltip} arrow enterTouchDelay={0} leaveTouchDelay={2500}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
            px: 1,
            py: 0.75,
            borderRadius: 1,
            border: '1px solid',
            borderColor: hasDetails ? `${detailColor}66` : '#ccc',
            backgroundColor: hasDetails ? `${detailColor}10` : 'transparent',
            cursor: 'help',
            maxWidth: '100%'
          }}
        >
          <DescriptionIcon sx={{ fontSize: compactIconSize, color: detailColor, mt: '2px', flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: detailColor,
                fontWeight: 600,
                lineHeight: 1.2,
                mb: 0.25
              }}
            >
              {detailLabel}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: compactFontSize,
                color: hasDetails ? 'text.primary' : '#999',
                lineHeight: 1.35,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                whiteSpace: 'normal',
                wordBreak: 'break-word'
              }}
            >
              {hasDetails ? preview : detailLabel}
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    );

    const snapshotType = log.widget_snapshot
      ? 'widget'
      : (log.timeevent_snapshot ? 'timeevent' : null);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {detailContent}
        {snapshotType && (
          <Button
            variant="text"
            size="small"
            onClick={() => handleOpenSnapshotDialog(log, snapshotType)}
            sx={{
              alignSelf: 'flex-start',
              textTransform: 'none',
              fontSize: compactFontSize,
              px: 0,
              minWidth: 0
            }}
          >
            {snapshotType === 'widget'
              ? t('actionsLogs.viewWidgetSnapshot', 'View widget snapshot')
              : t('actionsLogs.viewEventSnapshot', 'View event snapshot')}
          </Button>
        )}
      </Box>
    );
  };

  const renderMobileField = (label, content) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: '0.01em' }}>
        {label}
      </Typography>
      {content}
    </Box>
  );

  const getExecutionContextIcon = (context) => {
    switch (context) {
      case 'force_run':
        return <ForceRunIcon sx={{ fontSize: compactIconSize, color: primaryColor }} />;
      case 'automated_schedule':
        return <ScheduleIcon sx={{ fontSize: compactIconSize, color: '#666' }} />;
      case 'automated_replay':
        return <ReplayIcon sx={{ fontSize: compactIconSize, color: '#666' }} />;
      default:
        return null;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const normalizeTimestamp = (value) => {
      if (typeof value === 'number') return value;
      const raw = String(value).trim();
      if (!raw) return null;
      
      // Detect if timezone info already present (Z or +hh:mm / -hh:mm)
      const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
      if (hasTimezone) return raw;
      
      // Replace space between date/time with T for ISO compatibility
      const isoLike = raw.includes('T') ? raw : raw.replace(' ', 'T');
      return `${isoLike}Z`;
    };
    
    try {
      const normalized = normalizeTimestamp(timestamp);
      const date = normalized ? new Date(normalized) : new Date(timestamp);
      if (Number.isNaN(date.getTime())) {
        return timestamp;
      }
      return date.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
      });
    } catch {
      return timestamp;
    }
  };

  const formatDuration = (durationMs) => {
    if (durationMs === null || durationMs === undefined) return 'N/A';
    if (durationMs < 1000) {
      return `${Math.round(durationMs)}ms`;
    }
    return `${(durationMs / 1000).toFixed(1)}s`;
  };

  // Extract unique values for filters
  const uniqueActionTypes = [...new Set(logs.map(log => log.action_type).filter(Boolean))];
  const uniqueStatuses = [...new Set(logs.map(log => log.status).filter(Boolean))];
  const uniqueContexts = [...new Set(logs.map(log => log.execution_context).filter(Boolean))];

  const parseSnapshotPayload = (rawValue) => {
    if (!rawValue) return null;
    try {
      if (typeof rawValue === 'string') {
        return JSON.parse(rawValue);
      }
      return rawValue;
    } catch (err) {
      console.warn('ActionsLogsDialog: Failed to parse snapshot payload:', err);
      return null;
    }
  };

  const handleOpenSnapshotDialog = (log, snapshotType = 'widget') => {
    const rawSnapshot = snapshotType === 'widget'
      ? log.widget_snapshot
      : log.timeevent_snapshot;
    const parsed = parseSnapshotPayload(rawSnapshot);
    if (!parsed) {
      return;
    }
    setSnapshotContext({
      log,
      data: parsed,
      type: snapshotType
    });
    setSnapshotDialogOpen(true);
  };

  const handleCloseSnapshotDialog = () => {
    setSnapshotDialogOpen(false);
    setSnapshotContext(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            minHeight: maxHeight,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }
        }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${hoverColor} 100%)`,
          color: 'white',
          padding: '12px 16px',
          borderRadius: '16px 16px 0 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1, overflow: 'hidden' }}>
          <AssignmentIcon sx={{ marginRight: 1, fontSize: compactIconSize }} />
          <Tooltip title={title} arrow>
            <Typography
              variant="h6"
              component="div"
              noWrap
              sx={{
                fontSize: compactHeadFontSize,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.2,
              }}
            >
              {formatActionsLogsDialogTitle(title)}
            </Typography>
          </Tooltip>
        </Box>
        <CloseButton onClick={onClose} sx={{ color: 'white', flexShrink: 0, ml: 1 }} />
      </DialogTitle>

      <DialogContent sx={{ padding: '12px' }}>
        {/* Display Mode and Filters Section */}
        <Box sx={{ marginBottom: 2 }}>
          {/* Context Selection */}
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="subtitle1" sx={{ marginBottom: 0.5, fontWeight: 600, fontSize: compactHeadFontSize }}>
              Context Selection
            </Typography>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Project/Case</InputLabel>
                  <Select
                    value={selectedCaseId}
                    label="Project/Case"
                    onChange={(e) => {
                      setSelectedCaseId(e.target.value);
                      setError('');
                      setLogs([]);
                      setPage(1);
                      setCursor(null);
                      setNextCursor(null);
                      setPreviousCursors([]);
                    }}
                    disabled={loadingOptions}
                  >
                    <MenuItem value="">All Cases</MenuItem>
                    {availableCases.map(case_ => (
                      <MenuItem key={case_.id} value={case_.id}>
                        {case_.title || case_.name || `Case ${case_.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText sx={{ minHeight: '1.25rem' }}>&nbsp;</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="TimeEvent ID (optional)"
                  placeholder="Enter TimeEvent ID or leave empty for all"
                  value={selectedTimeventId}
                  onChange={(e) => {
                    setSelectedTimeventId(e.target.value);
                    setError('');
                    setLogs([]);
                    setPage(1);
                    setCursor(null);
                    setNextCursor(null);
                    setPreviousCursors([]);
                  }}
                  helperText="Leave empty to show all timeevents in the selected project"
                />
              </Grid>
            </Grid>
            <Box sx={{ marginTop: 0.75, display: 'flex', gap: 0.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  console.log('ActionsLogsDialog: Clear Selection clicked');
                  setSelectedCaseId('');
                  setSelectedTimeventId('');
                  setError('');
                  setLogs([]);
                  setPage(1);
                  setCursor(null);
                  setNextCursor(null);
                  setPreviousCursors([]);
                  // Trigger fetch of all actions after clearing selections
                  setTimeout(() => handlePageChange('reset'), 100);
                }}
                sx={{ 
                  borderRadius: '8px',
                  textTransform: 'none'
                }}
              >
                Clear Selection
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  console.log('ActionsLogsDialog: Apply Filters clicked - Current selections:', { selectedCaseId, selectedTimeventId });
                  handlePageChange('reset');
                }}
                sx={{ 
                  backgroundColor: primaryColor,
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: hoverColor }
                }}
              >
                {(!selectedCaseId && !selectedTimeventId) ? 'Show All Actions' : 'Apply Filters'}
              </Button>
            </Box>

            <Box
              sx={{
                marginTop: 0.75,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center'
              }}
            >
              <Typography variant="body2" color="textSecondary">
                {`Page ${paginationInfo.page} · ${logs.length} logs on this page`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={previousCursors.length === 0}
                  onClick={() => handlePageChange("prev")}
                  sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                  Previous
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!paginationInfo.hasMore || !nextCursor}
                  onClick={() => handlePageChange("next")}
                  sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Filters Section */}
          {showFilters && (
            <Box>
              <Typography variant="subtitle1" sx={{ marginBottom: 1, display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: compactHeadFontSize }}>
                <FilterIcon sx={{ marginRight: 1 }} />
                Filters
              </Typography>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={localFilters.status}
                      label="Status"
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {uniqueStatuses.map(status => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Action Type</InputLabel>
                    <Select
                      value={localFilters.actionType}
                      label="Action Type"
                      onChange={(e) => handleFilterChange('actionType', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {uniqueActionTypes.map(type => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Context</InputLabel>
                    <Select
                      value={localFilters.executionContext}
                      label="Context"
                      onChange={(e) => handleFilterChange('executionContext', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {uniqueContexts.map(context => (
                        <MenuItem key={context} value={context}>
                          {context.replace('_', ' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={applyFilters}
                      startIcon={<FilterIcon />}
                      sx={{ 
                        color: primaryColor, 
                        borderColor: primaryColor,
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: 'rgba(87, 87, 176, 0.05)',
                          borderColor: hoverColor
                        }
                      }}
                    >
                      Apply
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={clearFilters}
                      startIcon={<RefreshIcon />}
                      sx={{ 
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        }
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>

        {/* Content Section */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        ) : logs.length === 0 ? (
          <Box sx={{ textAlign: 'center', padding: 4 }}>
            <AssignmentIcon sx={{ fontSize: 48, color: '#ccc', marginBottom: 1.5 }} />
              <Typography variant="h6" color="textSecondary" sx={{ fontSize: compactHeadFontSize }}>
              No action logs found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {!selectedCaseId && !selectedTimeventId
                ? 'No action executions found across all actions.' 
                : 'No action executions match the current filters.'}
            </Typography>
          </Box>
        ) : isSmallScreen ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {logs.map((log, index) => (
              <Paper
                key={log.id || index}
                elevation={0}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  padding: '12px',
                  backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: compactFontSize, fontWeight: 600 }}>
                    {formatTimestamp(log.timestamp)}
                  </Typography>
                  {renderStatusIndicator(log.status)}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                  {renderMobileField(
                    'Type',
                    <Tooltip title={formatDisplayValue(log.action_type, { transformUnderscores: true })} arrow>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'help' }}>
                        {getActionTypeIcon(log.action_type)}
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: compactFontSize,
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {formatDisplayValue(log.action_type, { transformUnderscores: true })}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}

                  {renderMobileField(
                    'Context',
                    <Tooltip title={formatDisplayValue(log.execution_context, { transformUnderscores: true })} arrow>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'help' }}>
                        {getExecutionContextIcon(log.execution_context)}
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: compactFontSize,
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {formatDisplayValue(log.execution_context, { transformUnderscores: true })}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}

                  {renderMobileField(
                    'Duration',
                    <Tooltip title={`Duration: ${formatDuration(log.duration_ms)}`} arrow>
                      <Typography variant="body2" sx={{ fontSize: compactFontSize, cursor: 'help' }}>
                        {formatDuration(log.duration_ms)}
                      </Typography>
                    </Tooltip>
                  )}

                  {renderMobileField(
                    'File',
                    renderTooltipText(log.file_source, {
                      title: log.file_source || 'N/A',
                      sx: { wordBreak: 'break-word' }
                    })
                  )}

                  {renderMobileField(t('actionsLogs.details', 'Details'), renderLogDetails(log))}
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer 
            component={Paper} 
            elevation={0} 
            sx={{ 
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              overflow: 'hidden',
              overflowX: 'auto'
            }}
          >
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: tableMinWidth, fontSize: compactFontSize }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: columnWidths.timestamp, padding: compactCellPadding, fontSize: compactHeadFontSize }}>Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: columnWidths.type, padding: compactCellPadding, fontSize: compactHeadFontSize, textAlign: 'center' }} align="center">Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: columnWidths.status, textAlign: 'center', padding: compactCellPadding, fontSize: compactHeadFontSize }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: columnWidths.context, padding: compactCellPadding, fontSize: compactHeadFontSize }}>Context</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: columnWidths.duration, textAlign: 'right', padding: compactCellPadding, fontSize: compactHeadFontSize }} align="right">Duration</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: columnWidths.file, padding: compactCellPadding, fontSize: compactHeadFontSize }}>File</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: columnWidths.details, padding: compactCellPadding, fontSize: compactHeadFontSize }}>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log, index) => (
                  <TableRow
                    key={log.id || index}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                      '&:hover': { backgroundColor: '#f0f0ff' }
                    }}
                  >
                    <TableCell sx={{ width: columnWidths.timestamp, verticalAlign: 'middle' }}>
                      <Tooltip title={formatTimestamp(log.timestamp)} arrow>
                        <Typography variant="body2" sx={{ fontSize: compactFontSize, cursor: 'help' }}>
                          {formatTimestamp(log.timestamp)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: columnWidths.type, verticalAlign: 'middle', textAlign: 'center' }} align="center">
                      <Tooltip title={formatDisplayValue(log.action_type, { transformUnderscores: true })} arrow>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          {getActionTypeIcon(log.action_type)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: columnWidths.status, verticalAlign: 'middle', textAlign: 'center' }} align="center">
                      {renderStatusIndicator(log.status)}
                    </TableCell>
                    <TableCell sx={{ width: columnWidths.context, verticalAlign: 'middle' }}>
                      <Tooltip title={formatDisplayValue(log.execution_context, { transformUnderscores: true })} arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                          {getExecutionContextIcon(log.execution_context)}
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              marginLeft: 1, 
                              fontSize: compactFontSize,
                              maxWidth: 100, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap' 
                            }}
                          >
                            {formatDisplayValue(log.execution_context, { transformUnderscores: true })}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: columnWidths.duration, verticalAlign: 'middle', textAlign: 'right' }} align="right">
                      <Tooltip title={`Duration: ${formatDuration(log.duration_ms)}`} arrow>
                        <Typography variant="body2" sx={{ fontSize: compactFontSize, cursor: 'help' }}>
                          {formatDuration(log.duration_ms)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: columnWidths.file, verticalAlign: 'middle' }}>
                      <Tooltip title={log.file_source || 'No file source'} arrow>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: compactFontSize, 
                            maxWidth: 150, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            cursor: 'help' 
                          }}
                        >
                          {log.file_source || 'N/A'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: columnWidths.details, verticalAlign: 'middle' }}>
                      {renderLogDetails(log)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        padding: '12px', 
        background: 'rgba(247, 247, 255, 0.3)',
        borderTop: '1px solid rgba(87, 87, 176, 0.1)',
        justifyContent: 'space-between'
      }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
          {`Page ${paginationInfo.page} · Showing ${logs.length} logs (page size: ${paginationInfo.pageSize})`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={() => {
              console.log('ActionsLogsDialog: Refresh clicked');
              handlePageChange("refresh");
            }}
            startIcon={<RefreshIcon />}
            variant="outlined"
            sx={{ 
              color: 'rgba(87, 87, 176, 0.8)',
              borderColor: 'rgba(87, 87, 176, 0.3)',
              borderRadius: '8px',
              px: 2.5,
              py: 0.5,
              fontWeight: '500',
              textTransform: 'none',
              '&:hover': { 
                backgroundColor: 'rgba(87, 87, 176, 0.05)',
                borderColor: 'rgba(87, 87, 176, 0.5)',
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </DialogActions>
      </Dialog>
      
      <WidgetSnapshotDialog
        open={snapshotDialogOpen}
        onClose={handleCloseSnapshotDialog}
        snapshotContext={snapshotContext}
        formatTimestamp={formatTimestamp}
      />
    </>
  );
};

const getActionTypeIcon = (actionType) => {
  if (!actionType) return <DescriptionIcon sx={{ fontSize: compactIconSize, color: '#999' }} />;
  const type = (actionType || '').toLowerCase();
  switch (true) {
    case /widget/.test(type):
      return <WidgetsIcon sx={{ fontSize: compactIconSize, color: primaryColor }} />;
    case /scan|search|analysis|analyze/.test(type):
      return <SearchIcon sx={{ fontSize: compactIconSize, color: '#1565c0' }} />;
    case /upload|ingest|import/.test(type):
      return <CloudUploadIcon sx={{ fontSize: compactIconSize, color: '#1565c0' }} />;
    case /transform|process|parse|extract/.test(type):
      return <BuildIcon sx={{ fontSize: compactIconSize, color: '#6a1b9a' }} />;
    default:
      return <DescriptionIcon sx={{ fontSize: compactIconSize, color: '#666' }} />;
  }
};

export default ActionsLogsDialog;
